import { api, apiUpload, API_BASE } from '../lib/api';
import type { Contact, ContactFolder } from '../types';

// --- Contact helpers ---

function resolveUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${API_BASE}${url}`;
  return url;
}

function parseContact(raw: any): Contact {
  return {
    id: raw.id,
    firstName: raw.firstName ?? raw.first_name ?? null,
    lastName: raw.lastName ?? raw.last_name ?? null,
    nicknames: raw.nicknames ?? [],
    email: raw.email ?? null,
    phone: raw.phone ?? null,
    photoUrl: resolveUrl(raw.photoUrl ?? raw.photo_url ?? null),
    flags: raw.flags ?? 0,
    createdAt: new Date(raw.createdAt ?? raw.created_at),
    updatedAt: new Date(raw.updatedAt ?? raw.updated_at),
  };
}

export function getContactDisplayName(contact: Contact): string {
  if (contact.firstName && contact.lastName) return `${contact.firstName} ${contact.lastName}`;
  if (contact.firstName) return contact.firstName;
  if (contact.nicknames[0]) return contact.nicknames[0];
  return contact.email || 'Unknown';
}

export function getContactInitials(contact: Contact): string {
  if (contact.firstName && contact.lastName) {
    return `${contact.firstName[0]}${contact.lastName[0]}`.toUpperCase();
  }
  if (contact.firstName) return contact.firstName[0].toUpperCase();
  if (contact.nicknames[0]) return contact.nicknames[0][0].toUpperCase();
  if (contact.email) return contact.email[0].toUpperCase();
  return '?';
}

// --- Contacts CRUD ---

export async function listContacts(filter?: string, opts?: { folderId?: string; ungrouped?: boolean; starred?: boolean }): Promise<Contact[]> {
  const q = new URLSearchParams();
  if (filter) q.set('q', filter);
  if (opts?.folderId) q.set('folderId', opts.folderId);
  if (opts?.ungrouped) q.set('ungrouped', 'true');
  if (opts?.starred) q.set('starred', 'true');
  const qs = q.toString() ? `?${q}` : '';
  const results = await api<any[]>(`/api/contacts${qs}`);
  return results.map(parseContact);
}

export async function getContactFolders(): Promise<ContactFolder[]> {
  return api<ContactFolder[]>('/api/contact-folders');
}

export async function createContactFolder(name: string): Promise<ContactFolder> {
  return api<ContactFolder>('/api/contact-folders', { method: 'POST', body: JSON.stringify({ name }) });
}

export async function updateContactFolder(id: string, name: string): Promise<void> {
  await api(`/api/contact-folders/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
}

export async function deleteContactFolder(id: string): Promise<void> {
  await api(`/api/contact-folders/${id}`, { method: 'DELETE' });
}

export async function moveContactToFolder(contactId: string, folderId: string | null): Promise<void> {
  await api(`/api/contacts/${contactId}/folder`, { method: 'PATCH', body: JSON.stringify({ folderId }) });
}

export async function getContact(id: string): Promise<Contact> {
  const raw = await api<any>(`/api/contacts/${id}`);
  return parseContact(raw);
}

export async function createContact(data: {
  firstName?: string; lastName?: string; nicknames?: string[]; email?: string; phone?: string;
}): Promise<Contact> {
  const raw = await api<any>('/api/contacts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return parseContact(raw);
}

export async function updateContact(id: string, data: Partial<{
  firstName: string | null; lastName: string | null; nicknames: string[];
  email: string | null; phone: string | null;
}>): Promise<void> {
  await api(`/api/contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteContact(id: string): Promise<void> {
  await api(`/api/contacts/${id}`, { method: 'DELETE' });
}

export async function importContacts(contacts: Array<{
  firstName?: string;
  lastName?: string;
  nicknames?: string[];
  email?: string;
  phone?: string;
}>): Promise<{ imported: number; skipped: number }> {
  return api<{ imported: number; skipped: number }>('/api/contacts/import', {
    method: 'POST',
    body: JSON.stringify({ contacts }),
  });
}

export async function updateContactFlags(contactId: string, flags: number): Promise<void> {
  await api(`/api/contacts/${contactId}/flags`, {
    method: 'PATCH',
    body: JSON.stringify({ flags }),
  });
}

export async function uploadContactPhoto(contactId: string, file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiUpload(`/api/contacts/${contactId}/photo`, form);
  return res.photoUrl;
}

export async function setContactPhotoUrl(contactId: string, url: string): Promise<string> {
  const res = await api<{ photoUrl: string }>(`/api/contacts/${contactId}/photo`, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
  return res.photoUrl;
}

// --- Backward compatibility ---

/** @deprecated Use listContacts() instead */
export async function getSavedContacts(_uid: string): Promise<string[]> {
  const contacts = await listContacts();
  return contacts.filter(c => c.email).map(c => c.email!);
}

// --- Share history ---

export interface ShareRecipient {
  email: string;
  contactId?: string;
  contactName?: string;
  contactPhoto?: string;
}

export interface ShareHistoryEntry {
  id: string;
  resourceId: string;
  resourceType: 'item' | 'folder';
  recipients: ShareRecipient[];
  sharedAt: Date;
}

export async function getMyShares(_uid: string): Promise<ShareHistoryEntry[]> {
  const results = await api<any[]>('/api/shared/by-me');
  // The Worker returns one row per recipient; group by resourceId+sharedAt
  const grouped = new Map<string, ShareHistoryEntry>();
  for (const r of results) {
    const key = `${r.resourceId}-${r.sharedAt}`;
    const recipient: ShareRecipient = {
      email: r.recipientEmail,
      contactId: r.contactId || undefined,
      contactName: r.contactName || undefined,
      contactPhoto: r.contactPhoto ? resolveUrl(r.contactPhoto) || undefined : undefined,
    };
    const existing = grouped.get(key);
    if (existing) {
      existing.recipients.push(recipient);
    } else {
      grouped.set(key, {
        id: r.id,
        resourceId: r.resourceId,
        resourceType: r.resourceType,
        recipients: [recipient],
        sharedAt: new Date(r.sharedAt),
      });
    }
  }
  return Array.from(grouped.values());
}

export interface SharedWithMeEntry {
  id: string;
  resourceId: string;
  resourceType: 'item' | 'folder';
  sharerUid: string;
  sharedAt: Date;
}

export async function getSharedWithMe(_email: string): Promise<SharedWithMeEntry[]> {
  const items = await api<any[]>('/api/shared/with-me');
  return items.map((item) => ({
    id: item.id,
    resourceId: item.id,
    resourceType: 'item' as const,
    sharerUid: item.createdBy,
    sharedAt: new Date(item.sharedAt ?? item.createdAt),
  }));
}
